import React, { useLayoutEffect, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  List,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash";
import { makeStyles } from "tss-react/mui";

import client from "@/client";
import { Features } from "@/config/features";
import { graphql } from "@/gql";
import {
  AllergiesKind,
  FoodAddPortionDetailsFragment,
  FoodItemFragment,
  FoodSource,
} from "@/gql/graphql";
import useDebounce from "@/hooks/useDebounce";
import { colors } from "@/theme/overrides/colors";

import FoodItem from "./FoodItem";
import SearchIcon from "../Icons/SearchIcon";

interface Props {
  className?: string;
  clientAllergies?: AllergiesKind[];
  onClose: () => void;
  onAddFood: (
    food: FoodItemFragment,
    foodPortion: FoodAddPortionDetailsFragment,
    amount: number
  ) => void;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    borderRadius: 0,
    padding: theme.spacing(4, 3),
  },
  title: {
    fontSize: 18,
    lineHeight: 1,
    fontWeight: 700,
    color: colors.dark,
  },
  closeButton: {
    padding: theme.spacing(1),
    color: colors.grey,
    backgroundColor: "#E8EEF6",
  },
  checkboxLabel: {
    color: colors.grey,
  },
  addButton: {
    padding: theme.spacing(2),
    color: colors.grey,
    backgroundColor: "#E8EEF6",
  },
  helperText: {
    color: colors.grey,
    textAlign: "center",
  },
  list: {
    maxHeight: "calc(100vh - 290px)",
    overflow: "auto",
  },
}));

const LIMIT = 8;

const foodDatabaseQuery = graphql(`
  query FoodDatabase(
    $sources: [FoodSource!]!
    $name: String!
    $first: Int
    $after: String
  ) {
    foods(name: $name, sources: $sources, first: $first, after: $after) {
      nodes {
        ...FoodItem
      }
      pageInfo {
        endCursor
        hasNextPage
      }
    }
  }
`);

const FoodDatabase: React.FC<Props> = ({
  className,
  onClose,
  onAddFood,
  clientAllergies = [],
}) => {
  const { classes, cx } = useStyles();
  const [selectedSources, setSelectedSources] = useState({
    FOOD_DATABASE: false,
    RECIPES: true,
    CNC_GC: false,
    MCCANCE_AND_WIDOWSONS: false,
    USDA: true,
    FNRI: true,
  });
  const toggleSource = (source: keyof typeof selectedSources) => {
    setSelectedSources({
      ...selectedSources,
      [source]: !selectedSources[source],
    });
  };
  const [searchString, setSearchString] = useState("");
  // Waits 500 millisecond after the user finishes inputting in the search
  // text field before sending the searchFood query. This is to prevent query
  // from being sent every keystroke.
  const setSearch = useDebounce(setSearchString, 500);

  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<FoodItemFragment[]>([]);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const searchFoods = (cursor: string | null, loadMore: boolean) => {
    setLoading(true);
    setHasNextPage(false);
    client
      .query({
        query: foodDatabaseQuery,
        variables: {
          sources: Object.entries(selectedSources)
            .filter(([_, checked]) => checked)
            .map(([key]) => key) as FoodSource[],
          name: searchString,
          first: LIMIT,
          after: cursor,
        },
      })
      .then((result) => {
        if (loadMore) setResults(results.concat(result.data.foods.nodes));
        else setResults(result.data.foods.nodes);
        setHasNextPage(result.data.foods.pageInfo.hasNextPage);
        setEndCursor(result.data.foods.pageInfo.endCursor ?? null);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // `useLayoutEffect` is used instead of `useEffect` to avoid the no result
  // message being showed for a split second. If `useEffect` is used, there is
  // a point in time where `searchString` is not empty but `loading` is still
  // false, which will cause the no result message because of the message's
  // conditions. This happens because `useEffect` will render the component
  // after `searchString` is set, then run the side effect that runs the query,
  // which will set `loading` from false to true then render again. This doesn't
  // occur on `useLayoutEffect` as this hook will run the side effect first
  // before rendering if the dependencies changed.
  useLayoutEffect(() => {
    setEndCursor(null);
    if (searchString === "") {
      // don't search and display results when no search string
      setResults([]);
    } else {
      searchFoods(null, false);
    }
  }, [searchString, selectedSources]);

  const searchMore = () => {
    searchFoods(endCursor, true);
  };

  const noResults = !loading && searchString !== "" && isEmpty(results);

  return (
    <Paper className={cx(classes.root, className)} elevation={0}>
      <Stack spacing={4}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography className={classes.title}>Select Items</Typography>
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={onClose}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Grid container>
          {Features.foodDatabaseSourcesEnabled && (
            <>
              <Grid item xs={4}>
                <FormControlLabel
                  className={classes.checkboxLabel}
                  control={
                    <Checkbox
                      color="primary"
                      checked={selectedSources.FOOD_DATABASE}
                      onChange={() => toggleSource("FOOD_DATABASE")}
                    />
                  }
                  label="Food Database"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  className={classes.checkboxLabel}
                  control={
                    <Checkbox
                      color="primary"
                      checked={selectedSources.CNC_GC}
                      onChange={() => toggleSource("CNC_GC")}
                    />
                  }
                  label="CNC,GC"
                />
              </Grid>
              <Grid item xs={4}>
                <FormControlLabel
                  className={classes.checkboxLabel}
                  control={
                    <Checkbox
                      color="primary"
                      checked={selectedSources.MCCANCE_AND_WIDOWSONS}
                      onChange={() => toggleSource("MCCANCE_AND_WIDOWSONS")}
                    />
                  }
                  label="McCance & Widowsons"
                />
              </Grid>
            </>
          )}
          <Grid item xs={4}>
            <FormControlLabel
              className={classes.checkboxLabel}
              control={
                <Checkbox
                  color="primary"
                  checked={selectedSources.RECIPES}
                  onChange={() => toggleSource("RECIPES")}
                />
              }
              label="My Recipes"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              className={classes.checkboxLabel}
              control={
                <Checkbox
                  color="primary"
                  checked={selectedSources.USDA}
                  onChange={() => toggleSource("USDA")}
                />
              }
              label="USDA"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              className={classes.checkboxLabel}
              control={
                <Checkbox
                  color="primary"
                  checked={selectedSources.FNRI}
                  onChange={() => toggleSource("FNRI")}
                />
              }
              label="FNRI"
            />
          </Grid>
        </Grid>
        <Stack>
          <TextField
            placeholder="Search..."
            size="small"
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="inherit" />
                </InputAdornment>
              ),
            }}
          />
          <List className={classes.list}>
            {noResults && (
              <Stack direction="row" justifyContent="center">
                <Typography className={classes.helperText}>
                  No results were found for &quot;{searchString}&quot;
                </Typography>
              </Stack>
            )}
            {results.map((food) => (
              <FoodItem
                key={food.id}
                food={food}
                onAddFood={onAddFood}
                clientAllergies={clientAllergies}
              />
            ))}
            {loading && (
              <Stack direction="row" justifyContent="center">
                <CircularProgress variant="indeterminate" size={24} />
              </Stack>
            )}
            {hasNextPage && (
              <Stack direction="row" justifyContent="center">
                <Button
                  variant="outlined"
                  onClick={searchMore}
                  disabled={!hasNextPage}
                >
                  Load more
                </Button>
              </Stack>
            )}
          </List>
        </Stack>
      </Stack>
    </Paper>
  );
};

export default FoodDatabase;
